import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const DASHBOARD_TAB_STORAGE_KEY = 'rita-dashboard-active-tab'

export type DashboardTab =
  | 'dashboard'
  | 'reference-data'
  | 'connection-logs'
  | 'rules'

export interface ReferenceDataFilters {
  sourceIds: string | null
  goIds: string | null
  hasActiveFilters: boolean
}

export interface UseDashboardNavigationOptions {
  availableObjectValues: string[]
  onObjectTypeFromUrl?: (type: string | null) => void
}

export interface UseDashboardNavigationResult {
  activeTab: DashboardTab
  setActiveTab: (tab: DashboardTab) => void
  filters: ReferenceDataFilters
  clearFilters: () => void
}

/**
 * Hook to manage Dashboard URL navigation state including:
 * - Active tab with URL and localStorage persistence
 * - Reference data filters (source_ids, go_ids)
 * - Type param consumption for object selection
 * - Session storage navigation from external pages
 */
export function useDashboardNavigation({
  availableObjectValues,
  onObjectTypeFromUrl,
}: UseDashboardNavigationOptions): UseDashboardNavigationResult {
  const [params, setSearchParams] = useSearchParams()

  // Initialize active tab from localStorage or default to 'dashboard'
  const [activeTab, setActiveTabState] = useState<DashboardTab>(() => {
    const stored = localStorage.getItem(DASHBOARD_TAB_STORAGE_KEY)
    if (stored && isValidTab(stored)) {
      return stored as DashboardTab
    }
    return 'dashboard'
  })

  // Extract filter params
  const sourceIds = params.get('source_ids')
  const goIds = params.get('go_ids')
  const hasActiveFilters = !!(sourceIds || goIds)

  const filters: ReferenceDataFilters = {
    sourceIds,
    goIds,
    hasActiveFilters,
  }

  // Set active tab with URL and localStorage persistence
  const setActiveTab = useCallback(
    (tab: DashboardTab) => {
      if (tab === activeTab) return

      const newParams = new URLSearchParams(params.toString())
      newParams.set('tab', tab)
      setSearchParams(newParams)
      setActiveTabState(tab)
      localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, tab)
    },
    [activeTab, params, setSearchParams],
  )

  // Clear all reference data filters
  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams(params.toString())
    newParams.delete('source_ids')
    newParams.delete('go_ids')
    setSearchParams(newParams, { replace: true })
  }, [params, setSearchParams])

  // Sync URL params to state on mount or when params change
  useEffect(() => {
    const tabParam = params.get('tab')
    const typeParam = params.get('type')

    // Handle tab param
    if (tabParam && isValidTab(tabParam)) {
      setActiveTabState(tabParam as DashboardTab)
      localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, tabParam)
    }

    // Handle type param for object selection
    if (onObjectTypeFromUrl) {
      if (tabParam === 'reference-data') {
        if (typeParam && availableObjectValues.includes(typeParam)) {
          onObjectTypeFromUrl(typeParam)
        } else if (typeParam === 'unknown') {
          onObjectTypeFromUrl(null) // Show "All" view
        }
      } else if (typeParam && availableObjectValues.includes(typeParam)) {
        onObjectTypeFromUrl(typeParam)
      }
    }

    // Clear type param after consuming it
    if (typeParam) {
      const newParams = new URLSearchParams(params.toString())
      newParams.delete('type')
      setSearchParams(newParams, { replace: true })
    }
  }, [params, availableObjectValues, onObjectTypeFromUrl, setSearchParams])

  // Handle navigation from Crossroads Monitoring via sessionStorage
  useEffect(() => {
    const navigationData = sessionStorage.getItem(
      'integrationManagementNavigation',
    )
    if (!navigationData) return

    try {
      const payload = JSON.parse(navigationData)
      sessionStorage.removeItem('integrationManagementNavigation')

      if (payload.fromCrossroads) {
        if (payload.navigateToReferenceData) {
          setActiveTab('reference-data')
        } else if (payload.navigateToRules) {
          setActiveTab('rules')
        } else {
          setActiveTab('connection-logs')
        }
      }
    } catch (error) {
      console.error('Failed to parse navigation data:', error)
    }
  }, [setActiveTab])

  return {
    activeTab,
    setActiveTab,
    filters,
    clearFilters,
  }
}

function isValidTab(tab: string): tab is DashboardTab {
  return ['dashboard', 'reference-data', 'connection-logs', 'rules'].includes(
    tab,
  )
}
